/**
 * QA product slug -> table name whitelist.
 * All SQL queries use this map to construct table names — never user input directly.
 * Table names verified against actual database schema on 2026-03-03.
 */

export type ProductCategory =
  | "Post-Tensioning"
  | "Civil Grout"
  | "Flooring"
  | "Repair Mortars"
  | "Construction Grout"
  | "Peragrene"
  | "Toll Manufactured"
  | "Other";

export interface QAProductConfig {
  sample: string;
  config: string;
  label: string;
  category: ProductCategory;
  /** SQL LIKE pattern(s) for matching production_monitor_batch.product_lookup */
  productionLike?: string[];
}

export const QA_PRODUCTS: Record<string, QAProductConfig> = {
  "crosflow-835": {
    // #1622: Django displays crosflow830lc as "Crosflow 835" (rename layer).
    sample: "qa_production_crosflow830lcsample",
    config: "qa_production_crosflow830lcconfig",
    label: "Crosflow 835",
    category: "Flooring",
    productionLike: ["CROSFLOW 835%", "CROSFLOW 830 LC%"],
  },
  "crosflow-830": {
    sample: "qa_production_crosflow830sample",
    config: "qa_production_crosflow830config",
    label: "Crosflow 830",
    category: "Flooring",
    productionLike: ["CROSFLOW 830%"],
  },
  "pt-grout": {
    sample: "qa_production_ptgroutsample",
    config: "qa_production_ptgroutconfig",
    label: "PT Grout",
    category: "Post-Tensioning",
    productionLike: ["PT GROUT%"],
  },
  "ig-100": {
    sample: "qa_production_ig100sample",
    config: "qa_production_ig100config",
    label: "Infragrout 100",
    category: "Civil Grout",
    productionLike: ["INFRAGROUT 100%"],
  },
  "ig-110": {
    sample: "qa_production_ig110sample",
    config: "qa_production_ig110config",
    label: "Infragrout 110",
    category: "Civil Grout",
    // #5092 IG110NULL: Ostendo's plain 110 bag, item 210111 "InfraGrout 110
    // 20kg - (80)", is named whole. A pattern open after the pack size would
    // also take the Export and other pack-size bags, which stay unmapped here,
    // and would outrank a variant written after the pack size: "INFRAGROUT 110
    // 20KG%" (19) beats "INFRAGROUT 110%A+%" (16) on "110 20kg A+". The whole
    // name matches no LC, GREEN, A+ or TRIAL name, so no variant ever competes
    // with it in the #4760 ranking.
    productionLike: ["INFRAGROUT 110", "INFRAGROUT 110 +%", "INFRAGROUT 110 TRIAL%", "INFRAGROUT 110 20KG - (80)"],
  },
  // A+ additive pair (Akira 14 Aug: two distinct products, not one). Ostendo:
  //   110 A+     = items 210113/210114, desc "InfraGrout 110  A+"     (no LC)
  //   110 LC A+  = items 210117/210118, desc "InfraGrout 110  A+ LC"
  // QC templates cloned from Infragrout 110 (specs identical; Gus tunes limits
  // in the UI). Tables land via .jez/scripts/migrate-ig110-additive-qc-product.sql
  // (110 A+, existing) + migrate-ig110-lc-additive-qc-product.sql (110 LC A+, new);
  // the capability gate keeps each entry dormant until its tables exist.
  //
  // The most specific matching pattern wins (#4760), so the LC-A+ product (its
  // patterns require BOTH A+ and LC) outranks plain A+ (A+ only) and ig-110-lc
  // (LC only) on an "A+ LC" batch, and "A+" alone falls through to plain A+.
  // This is how the "A+ but not LC" case is expressed without a negative LIKE.
  //
  // productionLike UNVERIFIED against production: no A+ batch exists in
  // production_monitor_batch yet (probed 14 Aug) — confirm the real plant
  // strings when the first A+ batches are logged.
  "ig-110-lc-additive": {
    sample: "qa_production_ig110lcadditivesample",
    config: "qa_production_ig110lcadditiveconfig",
    label: "Infragrout 110 LC A+",
    category: "Civil Grout",
    // #5092: GREEN and LC are one designation (shared/product-aliases.ts), so
    // a GREEN A+ name is 110 LC A+. Both GREEN forms score 21, in either word
    // order and at any spacing ("110  A+ LC" is Ostendo's own). That outranks
    // ig-110-lc's "INFRAGROUT 110 GREEN%" (20), which beats both LC forms
    // here (18), and plain A+'s "INFRAGROUT 110%A+%" (16).
    productionLike: [
      "INFRAGROUT 110%A+%LC%",
      "INFRAGROUT 110%LC%A+%",
      "INFRAGROUT 110%GREEN%A+%",
      "INFRAGROUT 110%A+%GREEN%",
    ],
  },
  "ig-110-additive": {
    sample: "qa_production_ig110additivesample",
    config: "qa_production_ig110additiveconfig",
    label: "Infragrout 110 A+",
    category: "Civil Grout",
    productionLike: ["INFRAGROUT 110%A+%"],
  },
  "rs-2002": {
    sample: "qa_production_rs2002sample",
    config: "qa_production_rs2002config",
    // #1622: Django displays this table as "Peragrene" (rename layer) — label
    // follows what the lab reads daily; slug stays rs-2002 (existing links).
    label: "Peragrene (RS 2002)",
    category: "Peragrene",
  },
  // Same rule as the A+ pair above: crosflow-815's "CROSFLOW 810 LC%" outranks
  // plain crosflow-810's "CROSFLOW 810%", and likewise 835 over 830.
  "crosflow-815": {
    // #1622: Django's display-rename layer (migration 0085) shows the
    // crosflow810lc tables as "Crosflow 815" — there was never an 815 table.
    // This entry previously pointed at qa_production_crosflow815sample, which
    // does not exist; card rendered but entry would have failed.
    sample: "qa_production_crosflow810lcsample",
    config: "qa_production_crosflow810lcconfig",
    label: "Crosflow 815",
    category: "Flooring",
    productionLike: ["CROSFLOW 815%", "CROSFLOW 810 LC%"],
  },
  "crosflow-810": {
    sample: "qa_production_crosflow810sample",
    config: "qa_production_crosflow810config",
    label: "Crosflow 810",
    category: "Flooring",
    productionLike: ["CROSFLOW 810%"],
  },
  "crosfill-510": {
    sample: "qa_production_crosfill510sample",
    config: "qa_production_crosfill510config",
    label: "Crosfill 510",
    category: "Construction Grout",
    productionLike: ["CROSFILL 510%"],
  },
  "panfill": {
    sample: "qa_production_panfillsample",
    config: "qa_production_panfillconfig",
    label: "Panfill",
    category: "Post-Tensioning",
    productionLike: ["PANFILL%"],
  },
  "pocket-patch": {
    sample: "qa_production_pocketpatchsample",
    config: "qa_production_pocketpatchconfig",
    label: "Pocket Patch",
    category: "Post-Tensioning",
    productionLike: ["POCKET PATCH%"],
  },
  "crosflow-820": {
    sample: "qa_production_crosflow820sample",
    config: "qa_production_crosflow820config",
    label: "Crosflow 820",
    category: "Flooring",
    productionLike: ["CROSFLOW 820%"],
  },
  "crosfill-530": {
    sample: "qa_production_crosfill530sample",
    config: "qa_production_crosfill530config",
    label: "Crosfill 530",
    category: "Construction Grout",
    productionLike: ["CROSFILL 530%"],
  },
  // #666: Crosfill 570 was missing from the whitelist, so QC's 570 testing was
  // invisible in dashboards + digests (Gustavo flagged it on the 2026-06-25 digest).
  // Backing tables confirmed in DATABASE_SCHEMA.md: qa_production_crosfill570sample
  // (51 rows) + qa_production_crosfill570config (1 row). The 570rs variant is omitted
  // (0 rows in both rs tables — no data to surface yet).
  "crosfill-570": {
    sample: "qa_production_crosfill570sample",
    config: "qa_production_crosfill570config",
    label: "Crosfill 570",
    category: "Construction Grout",
    productionLike: ["CROSFILL 570%"],
  },
  "crosflow-855": {
    // Django rename layer: 855 IS the crosflow850lc table (0 rows to date).
    sample: "qa_production_crosflow850lcsample",
    config: "qa_production_crosflow850lcconfig",
    label: "Crosflow 855",
    category: "Flooring",
    productionLike: ["CROSFLOW 855%", "CROSFLOW 850 LC%"],
  },
  "crosflow-850": {
    sample: "qa_production_crosflow850sample",
    config: "qa_production_crosflow850config",
    label: "Crosflow 850",
    category: "Flooring",
    productionLike: ["CROSFLOW 850%"],
  },
  "ig-110-lc": {
    sample: "qa_production_ig110lcsample",
    config: "qa_production_ig110lcconfig",
    label: "Infragrout 110 LC",
    category: "Civil Grout",
    // #5092 IG110NULL: Ostendo sells 110 LC as item 210115, "InfraGrout 110
    // GREEN 20kg - (80)" (the plant product list pairs Infragrout 110 LC with
    // 210115), as ig-190-lc's 210116 below. The UPI matcher keeps its own GREEN
    // row (upi-product-match.ts AO_ITEM_PATTERNS) until PR 4800 merges.
    productionLike: ["INFRAGROUT 110 LC%", "INFRAGROUT 110 GREEN%"],
  },
  "ig-190": {
    sample: "qa_production_ig190sample",
    config: "qa_production_ig190config",
    label: "Infragrout 190",
    category: "Civil Grout",
    productionLike: ["INFRAGROUT 190%"],
  },
  "crosflow-840": {
    sample: "qa_production_crosflow840sample",
    config: "qa_production_crosflow840config",
    label: "Crosflow 840",
    category: "Flooring",
  },
  // #1622: the lab's daily Django products that were missing from this
  // whitelist entirely — every table below verified live in prod PG
  // (sample+config both present) 2026-08-17. Dormant/legacy Django tables
  // (Tier 3 in the issue) are deliberately NOT registered pending Gus's word.
  "crospatch-310": {
    sample: "qa_production_crospatch310sample",
    config: "qa_production_crospatch310config",
    label: "Crospatch 310 Lite",
    category: "Repair Mortars",
    productionLike: ["CROSPATCH 310%", "CROSPATCH LITE 310%"],
  },
  "crospatch-340": {
    sample: "qa_production_crospatch340sample",
    config: "qa_production_crospatch340config",
    label: "Crospatch 340",
    category: "Repair Mortars",
    productionLike: ["CROSPATCH 340%"],
  },
  "crospatch-370": {
    sample: "qa_production_crospatch370sample",
    config: "qa_production_crospatch370config",
    label: "Crospatch 370",
    category: "Repair Mortars",
    productionLike: ["CROSPATCH 370%"],
  },
  "crospatch-390": {
    sample: "qa_production_crospatch390sample",
    config: "qa_production_crospatch390config",
    label: "Crospatch 390 FC",
    category: "Repair Mortars",
    productionLike: ["CROSPATCH 390%"],
  },
  "peragrene-premix": {
    sample: "qa_production_peragrenepremixsample",
    config: "qa_production_peragrenepremixconfig",
    label: "Peragrene Premix",
    category: "Peragrene",
    productionLike: ["PERAGRENE PREMIX%"],
  },
  "peragrene-premix-export": {
    sample: "qa_production_peragrenepremixexportsample",
    config: "qa_production_peragrenepremixexportconfig",
    label: "Peragrene Premix Export",
    category: "Peragrene",
    productionLike: ["PERAGRENE PREMIX EXPORT%"],
  },
  "crosfill-510-530-premix": {
    sample: "qa_production_crosfill510530premixsample",
    config: "qa_production_crosfill510530premixconfig",
    label: "Crosfill 510/530 Premix",
    category: "Construction Grout",
  },
  "crosflow-815-binder": {
    sample: "qa_production_crosflow810lcbindersample",
    config: "qa_production_crosflow810lcbinderconfig",
    label: "Crosflow 815 Binder",
    category: "Flooring",
  },
  "crosflow-810-binder": {
    sample: "qa_production_crosflow810bindersample",
    config: "qa_production_crosflow810binderconfig",
    label: "Crosflow 810 Binder",
    category: "Flooring",
  },
  "crosflow-830-binder": {
    sample: "qa_production_crosflow830bindersample",
    config: "qa_production_crosflow830binderconfig",
    label: "Crosflow 830 Binder",
    category: "Flooring",
  },
  "crosflow-835-binder": {
    sample: "qa_production_crosflow830lcbindersample",
    config: "qa_production_crosflow830lcbinderconfig",
    label: "Crosflow 835 Binder",
    category: "Flooring",
  },
  "crosflow-850-binder": {
    sample: "qa_production_crosflow850bindersample",
    config: "qa_production_crosflow850binderconfig",
    label: "Crosflow 850 Binder",
    category: "Flooring",
  },
  "crosflow-premix": {
    sample: "qa_production_crosflowpremixsample",
    config: "qa_production_crosflowpremixconfig",
    label: "Crosflow Premix",
    category: "Flooring",
  },
  "crosflow-lc-premix": {
    sample: "qa_production_crosflowlcpremixsample",
    config: "qa_production_crosflowlcpremixconfig",
    label: "Crosflow LC Premix",
    category: "Flooring",
  },
  "ig-190-lc": {
    sample: "qa_production_ig190lcsample",
    config: "qa_production_ig190lcconfig",
    label: "Infragrout 190 LC",
    category: "Civil Grout",
    // #4776 GREENALIAS: Ostendo sells 190 LC as item 210116, "Infragrout 190
    // GREEN 20kg - (80)". The GREEN pattern is more specific than ig-190's
    // "INFRAGROUT 190%", so it wins (#4760). The UPI matcher keeps its own
    // GREEN rows (upi-product-match.ts AO_ITEM_PATTERNS); folding them into
    // this alias waits for PR 4800, which changes that file.
    productionLike: ["INFRAGROUT 190%LC%", "INFRAGROUT 190 GREEN%"],
  },
  // #1622/#1627 — the dormant/legacy Django tail, registered for 1:1 parity per
  // Akira's ruling. These SEED AS HIDDEN via qa_product_visibility (Gus un-hides
  // from the Specifications Archived section). Tables verified in prod PG.
  "crosfill-500": {
    sample: "qa_production_crosfill500sample",
    config: "qa_production_crosfill500config",
    label: "Crosfill 500",
    category: "Construction Grout",
    productionLike: ["CROSFILL 500%"],
  },
  "crosfill-500-binder": {
    sample: "qa_production_crosfill500bindersample",
    config: "qa_production_crosfill500binderconfig",
    label: "Crosfill 500 Binder",
    category: "Construction Grout",
  },
  "crosfill-570-rs": {
    sample: "qa_production_crosfill570rssample",
    config: "qa_production_crosfill570rsconfig",
    label: "Crosfill 570 RS",
    category: "Construction Grout",
    productionLike: ["CROSFILL 570 RS%"],
  },
  "crosfill-580-hes": {
    sample: "qa_production_crosfill580hessample",
    config: "qa_production_crosfill580hesconfig",
    label: "Crosfill 580 HES",
    category: "Construction Grout",
    productionLike: ["CROSFILL 580%"],
  },
  "crospatch-350": {
    sample: "qa_production_crospatch350sample",
    config: "qa_production_crospatch350config",
    label: "Crospatch 350",
    category: "Repair Mortars",
    productionLike: ["CROSPATCH 350%"],
  },
  "crosflow-855-binder": {
    sample: "qa_production_crosflow850lcbindersample",
    config: "qa_production_crosflow850lcbinderconfig",
    label: "Crosflow 855 Binder",
    category: "Flooring",
  },
  "gp-cement": {
    sample: "qa_production_gpcementsample",
    config: "qa_production_gpcementconfig",
    label: "GP Cement",
    category: "Other",
    productionLike: ["GP CEMENT%"],
  },
  "concrete": {
    sample: "qa_production_concretesample",
    config: "qa_production_concreteconfig",
    label: "Concrete",
    category: "Other",
  },
  "croscoat": {
    sample: "qa_production_croscoatsample",
    config: "qa_production_croscoatconfig",
    label: "Croscoat",
    category: "Other",
    productionLike: ["CROSCOAT%"],
  },
  "mortar-mix": {
    sample: "qa_production_mortarmixsample",
    config: "qa_production_mortarmixconfig",
    label: "Mortar Mix",
    category: "Other",
  },
  "rapid-set-concrete": {
    sample: "qa_production_rapidsetconcretesample",
    config: "qa_production_rapidsetconcreteconfig",
    label: "Rapid Set Concrete",
    category: "Other",
  },
  "sand-and-cement": {
    sample: "qa_production_sandandcementsample",
    config: "qa_production_sandandcementconfig",
    label: "Sand & Cement",
    category: "Other",
  },
  // #3681 FGTOLL: made for Crosbe by iChem, tested as finished goods. Their
  // tables are plant-PG DDL (.jez/scripts/migrate-3681-fgtoll-products.sql);
  // the capability gate keeps each card dark until its tables exist. Test sets
  // and the RM-QC material each one carries over from: shared/toll-manufactured.
  "primer-blue": {
    sample: "qa_production_primerbluesample",
    config: "qa_production_primerblueconfig",
    label: "Primer Blue",
    category: "Toll Manufactured",
  },
  "primer-pink": {
    sample: "qa_production_primerpinksample",
    config: "qa_production_primerpinkconfig",
    label: "Primer Pink",
    category: "Toll Manufactured",
  },
  "citric-acid-30": {
    sample: "qa_production_citricacid30sample",
    config: "qa_production_citricacid30config",
    label: "Citric Acid 30%",
    category: "Toll Manufactured",
  },
} as const;

/**
 * Get validated QA product config or null if invalid slug.
 */
export function getQAProduct(slug: string): QAProductConfig | null {
  return QA_PRODUCTS[slug] ?? null;
}

/**
 * All valid product slugs.
 */
export const QA_PRODUCT_SLUGS = Object.keys(QA_PRODUCTS);

/**
 * Find the QA product slug that matches a production product_lookup name: the
 * product with the most specific matching pattern (matchQASlugsForProductionName).
 * Returns null if no match found.
 */
export function findQASlugForProductionName(productLookup: string): string | null {
  return matchQASlugsForProductionName(QA_PRODUCTS, productLookup)[0] ?? null;
}

/**
 * One production-name pattern against one name, the same way the SQL-side
 * ILIKE reads it. Regex metacharacters are escaped FIRST or a literal "+" in a
 * pattern (e.g. "INFRAGROUT 110 +%") becomes a quantifier and over-matches the
 * whole product family, silently diverging from the ILIKE these patterns also
 * feed.
 */
export function productionLikeMatches(pattern: string, name: string): boolean {
  const regex = new RegExp(
    "^" +
      pattern
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/%/g, ".*")
        .replace(/_/g, ".") +
      "$",
    "i"
  );
  return regex.test(name.toUpperCase().trim());
}

/**
 * #4760: how specific a pattern is, as its literal characters (the % and _
 * wildcards not counted). A variant's pattern names its family and more:
 * "INFRAGROUT 190%LC%" (16) over "INFRAGROUT 190%" (14).
 */
export function productionLikeSpecificity(pattern: string): number {
  return pattern.replace(/[%_]/g, "").length;
}

/**
 * #4314: every slug in `products` whose productionLike patterns match the
 * name, most specific first, so the first element is the alias layer's
 * answer. findQASlugForProductionName takes it over the static map; the UPI
 * auto-create job passes the registry-aware catalog.
 *
 * #4760 IG190LCSLUG: a slug ranks by its most specific matching pattern, and
 * map order only breaks a tie. Map order alone gave "INFRAGROUT 190 LC" to
 * ig-190, which sits before ig-190-lc; the variant now wins wherever it sits.
 */
export function matchQASlugsForProductionName(
  products: Record<string, QAProductConfig>,
  productLookup: string,
): string[] {
  const hits: Array<{ slug: string; rank: number }> = [];
  for (const [slug, config] of Object.entries(products)) {
    let rank = -1;
    for (const pattern of config.productionLike ?? []) {
      if (productionLikeMatches(pattern, productLookup)) rank = Math.max(rank, productionLikeSpecificity(pattern));
    }
    if (rank >= 0) hits.push({ slug, rank });
  }
  // Array sort is stable, so equal ranks keep the map's order.
  return hits.sort((a, b) => b.rank - a.rank).map((h) => h.slug);
}

/**
 * #4760: the variants map order used to give to their base product, each with
 * that base. A batch release written before #4760 for one of these variants'
 * batches is stored under the base slug, and batch-release.ts reads it from
 * there. This exists ONLY for rows written before #4760; it goes once those
 * rows are counted and corrected under AKIRAD1. Nothing new is written
 * through it.
 */
export const PRE_4760_VARIANT_BASE_SLUGS: Readonly<Record<string, string>> = {
  "ig-190-lc": "ig-190",
  "crosfill-570-rs": "crosfill-570",
  "peragrene-premix-export": "peragrene-premix",
};

// #1989: the SPC test-parameter maps (mapTestToColumnExpr /
// mapTestToNotNullCondition / mapTestToSpecPrefix) that lived here were a
// second, hand-typed naming scheme that only knew the tests their author
// listed. Test keys, SQL fragments and spec bases now come from the registry
// in shared/qa-columns, applied to the product's real columns by
// server/lib/qa-test-registry (resolveProductTest / listProductTests).

/**
 * #1701: qa_production_upi.product stores the raw sample-table name (with or
 * without the qa_production_ prefix, e.g. "crosfill510sample" or
 * "qa_production_crosfill510sample"), not a QA_PRODUCTS slug. Resolve it to
 * the product's display label; fall back to the raw value so an unmapped
 * table stays visible rather than blank.
 */
export function sampleTableLabel(raw: string): string {
  const norm = raw.startsWith("qa_production_") ? raw : `qa_production_${raw}`;
  for (const p of Object.values(QA_PRODUCTS)) {
    if (p.sample === norm) return p.label;
  }
  return raw;
}

/**
 * #1704 (Gus 18/08): the product-family section order for every family-grouped
 * card grid — Test Entry, Finished Goods Analytics (Control Charts picker),
 * Specifications. ONE constant so the order can't drift per page.
 */
export const CATEGORY_ORDER: ProductCategory[] = [
  "Post-Tensioning",
  "Civil Grout",
  "Repair Mortars",
  "Construction Grout",
  "Flooring",
  "Peragrene",
  // #3681: toll-manufactured products sit after Crosbe's own families.
  "Toll Manufactured",
  "Other",
];

export function compareCategories(a: string, b: string): number {
  const ia = CATEGORY_ORDER.indexOf(a as ProductCategory);
  const ib = CATEGORY_ORDER.indexOf(b as ProductCategory);
  // Unknown categories sink to the end but stay visible, in stable name order.
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

/**
 * #1730: products renamed the #1622 way keep their HISTORY in the original
 * tables — the QA_PRODUCTS entry points the new name at the old table. This
 * derives that list (slug ≠ table basename) so the analyst's schema context
 * can state each mapping instead of concluding "data missing". Generated from
 * the registry on import — never hand-kept, so a future rename that repoints
 * an entry is announced automatically.
 */
export function renamedQaProducts(): Array<{ slug: string; label: string; sampleTable: string; configTable: string }> {
  const out: Array<{ slug: string; label: string; sampleTable: string; configTable: string }> = [];
  for (const [slug, p] of Object.entries(QA_PRODUCTS)) {
    const base = p.sample.replace(/^qa_production_/, "").replace(/sample$/, "");
    if (slug.replace(/-/g, "") !== base) {
      out.push({ slug, label: p.label, sampleTable: p.sample, configTable: p.config });
    }
  }
  return out;
}

/** A sample table's slug as the analyst prompt writes it: qa_production_{slug}sample. */
export function qaSampleTableSlug(sampleTable: string): string {
  return sampleTable.replace(/^qa_production_/, "").replace(/sample$/, "");
}

/**
 * #4327: a prompt slug ("rs2002") with the label products.ts gives its table
 * ("rs2002 = Peragrene (RS 2002)"), so the analyst can link a product name to
 * a slug whose name does not say it. A slug with no entry stays bare.
 */
export function qaSlugWithLabel(slug: string): string {
  const p = Object.values(QA_PRODUCTS).find((c) => qaSampleTableSlug(c.sample) === slug);
  return p ? `${slug} = ${p.label}` : slug;
}

const productWords = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * #4327: the QC sample tables products.ts holds for a live product name. A
 * label matches whole ("Peragrene Premix"), or by either part of a label
 * with a bracketed alias ("Peragrene (RS 2002)" for "Peragrene" or "RS 2002").
 * Failing that, the productionLike patterns (the most specific match). Read
 * from the labels only: which table QC treats as canonical is not decided here.
 */
export function qcSampleTablesForProductName(name: string): string[] {
  const wanted = productWords(name);
  if (!wanted) return [];
  const tables = Object.values(QA_PRODUCTS)
    .filter((p) => {
      const m = /^(.*?)\s*\((.+)\)\s*$/.exec(p.label);
      const forms = m ? [p.label, m[1], m[2]] : [p.label];
      return forms.some((f) => productWords(f) === wanted);
    })
    .map((p) => p.sample);
  if (tables.length > 0) return [...new Set(tables)];
  const slug = findQASlugForProductionName(name);
  return slug ? [QA_PRODUCTS[slug].sample] : [];
}
